import {
  Button,
  Card,
  Col,
  Create,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Space,
  Tag,
  notification,
  useForm,
} from '@pankod/refine-antd';
import { useNavigation } from '@pankod/refine-core';
import { ProductReturnTable } from 'components';
import { SingleDatePicker } from 'components/date-picker/SingleDatePicker';
import { ModuleExpenseDrawer } from 'components/drawer/ModuleExpenseDrawer';
import dayjs, { Dayjs } from 'dayjs';
import { EReturnStatus } from 'enums';
import { IReturn, ISale } from 'interfaces';
import { useEffect, useReducer, useState } from 'react';
import { ActionKind, initialState, productReturnReducer } from 'reducers/product-return-reducer';
import { ICreateExpenseReturnType } from 'services/expense';
import { deleteExpenseCategory } from 'services/expense-category';
import { createReturn, getSalesFromWarehouse } from 'services/return';
import { getSale } from 'services/sale';
import { getWarehousesDropdown, IWarehouseDropDown } from 'services/warehouse';

const { Option } = Select;

export const ReturnCreate: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [isWarehouseLoading, setIsWarehouseLoading] = useState<boolean>(false);
  const [warehouses, setWarehouses] = useState<IWarehouseDropDown[]>([]);
  const [isInvoiceLoading, setIsInvoiceLoading] = useState<boolean>(false);
  const [invoiceIds, setInvoiceIds] = useState<string[]>([]);
  const [returnDate, setReturnDate] = useState<Date>();
  const [invoiceSalesDate, setInvoiceSalesDate] = useState<Date>();
  const [isExpenseVisible, setIsExpenseVisible] = useState(false);
  const [expenses, setExpenses] = useState<ICreateExpenseReturnType[]>([]);
  const [saleProducts, setSaleProducts] = useState<ISale['salesProductsCopy']>([]);
  const [invoiceDiscount, setInvoiceDiscount] = useState<number>(0);
  const [{ returnProducts }, dispatch] = useReducer(productReturnReducer, initialState);
  const { list } = useNavigation();
  const { form, formProps, saveButtonProps, queryResult } = useForm<IReturn>({
    metaData: {
      fields: ['id'],
    },
  });

  useEffect(() => {
    (async () => {
      setIsFetching(true);
      try {
        const warehousesResponse = await getWarehousesDropdown();
        const warehousesData = warehousesResponse.map((warehouse) => ({
          id: warehouse.id,
          name: warehouse.name,
        }));
        setWarehouses(warehousesData);
      } catch (err: any) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
      setIsFetching(false);
    })();
  }, []);

  const addExpenses = (createdExpense: ICreateExpenseReturnType) => {
    setExpenses((prevExpenses) => {
      return [...prevExpenses, createdExpense];
    });
  };

  const getOptions = (items: IWarehouseDropDown[]) => {
    return items.map((item) => (
      <Option key={item.id} value={item.id}>
        {item.name}
      </Option>
    ));
  };

  const onSelectWarehouse = async (warehouseId: string | undefined) => {
    if (!warehouseId) return;

    // Reset all fields
    form.resetFields(['invoice', 'clientId', 'clientName']);
    dispatch({ type: ActionKind.SetReturnProducts, payload: [] });
    setInvoiceIds([]);

    setIsWarehouseLoading(true);
    try {
      const sales = await getSalesFromWarehouse(warehouseId);
      const invoiceIds = sales.map((sale) => sale.id);
      setInvoiceIds(invoiceIds);
    } catch (err: any) {
      setInvoiceIds([]);
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsWarehouseLoading(false);
  };

  const onSelectInvoice = async (invoiceId: string) => {
    // Reset fields
    dispatch({ type: ActionKind.SetReturnProducts, payload: [] });
    setSaleProducts([]);

    setIsInvoiceLoading(true);
    try {
      const saleInfo = await getSale(invoiceId);

      setInvoiceSalesDate(saleInfo?.salesDate);
      setSaleProducts(saleInfo?.salesProductsCopy ?? []);
      setInvoiceDiscount(saleInfo?.discountCost || 0);
      const clientInfo = saleInfo?.client;
      if (clientInfo) {
        form.setFieldsValue({ clientId: clientInfo.id, clientName: clientInfo.name });
      }
    } catch (err: any) {
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsInvoiceLoading(false);
  };

  const onFinish = async (values: any) => {
    setIsSubmitting(true);

    if (
      dayjs(dayjs(returnDate).format('YYYY/MM/DD')).isBefore(
        dayjs(invoiceSalesDate).format('YYYY/MM/DD'),
      )
    ) {
      notification.error({
        message: 'Failed',
        description: 'The return date must be in after or equal sale date.',
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const returnData = {
        warehouse: values.warehouse as string,
        salesCopy: values.invoice,
        client: values.clientId,
        note: values.note,
        returnStatus: EReturnStatus.CONFIRMED,
        returnDate: values.returnDate ? new Date(values.returnDate) : new Date(),
        returnProducts: returnProducts.map((returnProduct) => ({
          productVariant: returnProduct.productVariantId,
          salesProductCopy: returnProduct.salesProduct,
          itemPrice: returnProduct.unitPrice,
          returnQuantity: returnProduct.quantity,
          lossAmount: returnProduct.lossAmount,
        })),
        returnExpense: expenses.map((expense) => ({
          id: expense.id,
        })),
      };
      await createReturn(returnData);
      notification.success({
        message: 'Success',
        description: 'Successfully created a return.',
      });
      list('returns');
    } catch (err: any) {
      console.error(err);
      notification.error({
        message: 'Failed',
        description: err.message,
      });
    }
    setIsSubmitting(false);
  };

  const renderedExpenses = expenses.map((expense) => {
    const paymentMethodBankName = expense.paymentMethod ? `-${expense.paymentMethod.bankName}` : '';
    const tagString = `${expense.category.name}${paymentMethodBankName}-${expense.amount}`;

    return (
      <Tag
        key={expense.id}
        closable
        onClose={() => {
          try {
            deleteExpenseCategory(expense.id);
            setExpenses((prevExpenses) => {
              return prevExpenses.filter((item) => item.id !== expense.id);
            });
          } catch (err) {
            if (err instanceof Error) {
              notification.error({
                message: 'Failed',
                description: err.message,
              });
            }
          }
        }}
      >
        {tagString}
      </Tag>
    );
  });

  return (
    <>
      <ModuleExpenseDrawer
        onClose={() => setIsExpenseVisible(false)}
        visible={isExpenseVisible}
        addExpenses={addExpenses}
        title="Add Expense"
      />
      <Create
        title="Create Return"
        isLoading={queryResult?.isFetching}
        saveButtonProps={{
          ...saveButtonProps,
          disabled: !returnProducts.length,
          loading: isSubmitting,
        }}
      >
        <Form
          {...formProps}
          layout="vertical"
          initialValues={{
            isActive: true,
          }}
          onFinish={onFinish}
        >
          <Row justify="space-around" align="middle">
            <Col xs={24} lg={18}>
              <Card title="Return Information">
                <Form.Item
                  label="Warehouse"
                  name="warehouse"
                  rules={[{ required: true, message: 'Please select a warehouse' }]}
                >
                  <Select
                    showSearch
                    placeholder="Select a warehouse"
                    loading={isFetching || isWarehouseLoading}
                    disabled={isFetching || isWarehouseLoading || !warehouses.length}
                    onSelect={(value: string) => onSelectWarehouse(value?.toString())}
                    filterOption={(input, option: any) =>
                      option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {getOptions(warehouses)}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Invoice ID"
                  name="invoice"
                  rules={[{ required: true, message: 'Please select an invoice' }]}
                >
                  <Select
                    showSearch
                    placeholder="Select an invoice"
                    onSelect={onSelectInvoice}
                    loading={isWarehouseLoading || isInvoiceLoading}
                    disabled={isInvoiceLoading || !invoiceIds.length}
                    filterOption={(input, option: any) =>
                      option!.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {invoiceIds.map((id) => (
                      <Option key={id} value={id}>
                        {id}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item label="Client ID" name="clientId" style={{ display: 'none' }}>
                  <Input disabled />
                </Form.Item>
                <Form.Item label="Client" name="clientName">
                  <Input disabled />
                </Form.Item>
                <Form.Item label="Date" name="returnDate">
                  <SingleDatePicker
                    value={dayjs(returnDate)}
                    onChange={(value: Dayjs) => setReturnDate(new Date(value.toDate()))}
                  />
                </Form.Item>
                <Form.Item label="Note" name="note">
                  <Input.TextArea rows={4} />
                </Form.Item>
              </Card>
            </Col>
          </Row>

          <Divider />

          <Row justify="space-around" align="middle">
            <Col xs={24} lg={18}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Button onClick={() => setIsExpenseVisible(true)} size="large">
                  Add Expense
                </Button>
              </div>
            </Col>
          </Row>

          <Row justify="center" style={{ marginTop: '1rem' }}>
            <Col span={12}>
              <Space size={[8, 16]} wrap>
                {renderedExpenses}
              </Space>
            </Col>
          </Row>

          <Divider />

          <Row justify="space-around" align="middle">
            <Col xs={24} lg={18}>
              <ProductReturnTable
                options={saleProducts}
                returnProducts={returnProducts}
                productDispatcher={dispatch}
                invoiceDiscount={invoiceDiscount}
              />
            </Col>
          </Row>
        </Form>
      </Create>
    </>
  );
};
